import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, ComposedChart
} from 'recharts';
import {
  TrendingUp, Package, ShoppingCart, Users, AlertTriangle,
  Search, Filter, RefreshCw, ChevronDown, Download, Info
} from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import Card from '../../../components/common/Card/Card';
import Button from '../../../components/common/Button/Button';
import customerProductAnalyticsService from '../../../api/crm/analytics/customerProductAnalyticsService';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const CRMProductAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    range: '30days',
    category: '',
    region: '',
    product: ''
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await customerProductAnalyticsService.getAnalytics(filters);
        if (response.success) {
          setData(response.data);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError('An unexpected error occurred while fetching analytics.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [filters]);

  const fetchData = () => {
    // Manually trigger a refresh by creating a new filters object
    setFilters(prev => ({ ...prev }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 bg-gray-50 min-h-screen ${loading ? 'opacity-60 cursor-wait' : ''}`}>
      <PageHeader
        title="CRM Product Analytics"
        subtitle="Analyze product performance, inventory, and sales trends."
        breadcrumbs={[
          { label: 'Customer Relationship', path: '#' },
          { label: 'Product Analytics', path: '/customer-relationship/product-analytics' }
        ]}
      />

      {error && (
        <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl flex items-center gap-3 text-rose-600">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm font-medium">{error}</span>
          <Button variant="outline" size="sm" onClick={fetchData} className="ml-auto text-rose-600 border-rose-200 hover:bg-rose-100">
            Retry
          </Button>
        </div>
      )}

      {/* Filters Section */}
      <div className="bg-white p-4 rounded-xl shadow-sm flex flex-wrap items-center gap-4 border border-gray-100">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 mr-2">Filters: ({filters.range})</span>
        </div>

        <select
          name="range"
          value={filters.range}
          onChange={handleFilterChange}
          className="bg-gray-50 border-gray-200 rounded-lg text-sm px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500/20"
        >
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
          <option value="90days">Last 90 Days</option>
          <option value="365days">Last Year</option>
        </select>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Revenue', value: formatCurrency(data?.kpis?.totalRevenue || 0), icon: TrendingUp, color: 'emerald' },
          { label: 'Average Order Value', value: formatCurrency(data?.kpis?.totalRevenue / (data?.kpis?.totalOrders || 1)), icon: ShoppingCart, color: 'blue' },
          { label: 'Units Sold', value: (data?.kpis?.unitsSold || 0).toLocaleString(), icon: Package, color: 'amber' },
          { label: 'Active Products', value: (data?.kpis?.activeProducts || 0).toLocaleString(), icon: Users, color: 'purple' },
          { label: 'Low Stock Alert', value: (data?.inventoryAnalytics?.totalLowStock || 0).toString(), icon: AlertTriangle, color: 'red' }
        ].map((kpi, idx) => (
          <div key={idx} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-start justify-between cursor-default hover:shadow-md transition-all duration-300">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">{kpi.label}</p>
              <h3 className="text-xl font-bold text-gray-900">{kpi.value}</h3>
            </div>
            <div className={`p-2 rounded-xl bg-${kpi.color}-50 text-${kpi.color}-500`}>
              <kpi.icon className="w-5 h-5" />
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend */}
        <Card title="Sales Revenue Trend" className="min-h-[400px]">
          <div className="h-[320px] w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data?.trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(val) => `₹${val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val}`}
                />
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(val) => [formatCurrency(val), 'Revenue']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="#10b98120" strokeWidth={3} />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981' }} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Top Products */}
        <Card title="Top Performing Products (by Revenue)" className="min-h-[400px]">
          <div className="h-[320px] w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.productPerformance} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" hide />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="#64748b"
                  fontSize={11}
                  width={150}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(val) => [formatCurrency(val), 'Revenue']}
                />
                <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Category Breakdown */}
        <Card title="Revenue by Category" className="min-h-[400px]">
          <div className="h-[320px] w-full flex flex-col items-center justify-center">
            <ResponsiveContainer width="100%" height="80%">
              <PieChart>
                <Pie
                  data={data?.categoryAnalytics}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="revenue"
                  nameKey="category"
                >
                  {data?.categoryAnalytics?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(val) => [formatCurrency(val), 'Revenue']}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Region Performance */}
        <Card title="Region/State Sales Distribution" className="min-h-[400px]">
          <div className="h-[320px] w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.regionAnalytics}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="region" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `₹${val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val}`} />
                <Tooltip
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(val) => [formatCurrency(val), 'Revenue']}
                />
                <Bar dataKey="revenue" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Inventory & Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inventory Analytics */}
        <Card title="Inventory Health Status" icon={<Package className="w-5 h-5 text-emerald-500" />}>
          <div className="mt-4 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Low Stock
                </span>
                <span className="text-xs text-gray-500">{data?.inventoryAnalytics?.totalLowStock} items</span>
              </div>
              <div className="max-h-[150px] overflow-auto border border-gray-100 rounded-lg">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gray-600">Product</th>
                      <th className="px-3 py-2 font-medium text-gray-600 text-right">Stock</th>
                      <th className="px-3 py-2 font-medium text-gray-600 text-right">MRP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data?.inventoryAnalytics?.lowStock?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-900">{item.name}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-bold">{item.totalStock}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(item.mrp || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" /> Near Expiry (Next 90 Days)
                </span>
                <span className="text-xs text-gray-500">{data?.inventoryAnalytics?.totalNearExpiry} batches</span>
              </div>
              <div className="max-h-[150px] overflow-auto border border-gray-100 rounded-lg">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gray-600">Product</th>
                      <th className="px-3 py-2 font-medium text-gray-600 text-right">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data?.inventoryAnalytics?.nearExpiry?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-900">{item.name}</td>
                        <td className="px-3 py-2 text-right text-amber-600 font-bold">{item.nearExpiry}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Card>

        {/* AI Insights Section */}
        <Card title="Product Insights & Suggestions" icon={<TrendingUp className="w-5 h-5 text-emerald-500" />}>
          <div className="mt-4 space-y-3">
            {data?.insights?.map((insight, idx) => (
              <div key={idx} className={`p-4 rounded-xl border flex items-start gap-3 ${insight.type === 'success' ? 'bg-emerald-50 border-emerald-100' :
                insight.type === 'warning' ? 'bg-amber-50 border-amber-100' : 'bg-rose-50 border-rose-100'
                }`}>
                <div className={`p-1.5 rounded-lg ${insight.type === 'success' ? 'bg-emerald-500' :
                  insight.type === 'warning' ? 'bg-amber-500' : 'bg-rose-500'
                  } text-white`}>
                  {insight.type === 'success' ? <TrendingUp className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                </div>
                <p className="text-sm font-medium text-gray-800 leading-relaxed">
                  {insight.text}
                </p>
              </div>
            ))}
            {(!data?.insights || data.insights.length === 0) && (
              <p className="text-gray-500 text-sm italic">No insights available for the selected period.</p>
            )}
            <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 mt-6 text-xs text-center text-gray-500">
              <p>// TODO: Requires additional data for Margin and Prescription analysis.</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CRMProductAnalytics;
